import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  TablePagination,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Box } from "@mui/system";
import React, { MouseEvent, useEffect, useRef, useState } from "react";
import { formatDate } from "../../../utils/dateFormatUtils";
import LoaderBackdrop from "../../../components/ui/LoaderBackdrop";
import service from "../../../services/api/service";
import PaperCard from "../../../components/ui/PaperCard";
import { useSelector } from "react-redux";
import { RootState } from "../../../services/redux/store";

import { useDispatch } from "react-redux";
import { cleardisbursementButtonSlice } from "../../../services/redux/slices/disbursementButtonSlice";
import HeadingLeftRight from "../../../components/ui/HeadingLeftRight";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import postsServices from "../../../services/api/service/postsService";

interface Column {
  id: string;
  label: string;
  format?: (value: any) => string; // Ensure format always returns a string
}

export interface RowData {
  [key: string]: string | number | null;
}
interface DisbursementHistoryTableProps {
  showAllData: boolean;
  isLoading?: boolean;
}

const StyledTableCellHead = styled(TableCell)(({ theme }) => ({
  padding: "11px 6px",
  color: "#168794",
  fontSize: "12px",
  fontWeight: 600,
  lineHeight: "19px",
  textTransform: "capitalize",
  fontFamily: "Jost",
  textAlign: "left",
  width: "200px",
  "&:first-of-type": { paddingLeft: "10px" },
}));

const StyledTableCellBody = styled(TableCell)(({ theme }) => ({
  padding: "6px",
  color: "#707579",
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: "22px",
  textAlign: "left",
  width: "200px",
  "&:first-of-type": { paddingLeft: "10px" },
}));

const StatusBadge = styled("span")<{
  statusColor: string;
  backgroundColor: string;
}>(({ statusColor, backgroundColor }) => ({
  color: statusColor,
  textTransform: "capitalize",
  backgroundColor,
  padding: "5px 10px",
  borderRadius: "4px",
}));

const DisbursementHistoryTable: React.FC<DisbursementHistoryTableProps> = ({
  showAllData = false,
}) => {
  const [rows, setRows] = useState<RowData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const dispatch = useDispatch();

  const selectedLoanNumber = useSelector(
    (state: RootState) => state?.dashboardData?.selectedLoanNumber
  );
  const historyData = useSelector(
    (state: RootState) => state?.historyData?.history
  );
  //const isInitialMount = useRef(true);

  useEffect(() => {
    // if (isInitialMount.current) {
    //   isInitialMount.current = false;
    //   return;
    // }

    const fetchDataHistoryTable = async () => {
      setIsLoading(true);

      if (selectedLoanNumber) {
        const payload = { lan: selectedLoanNumber };
        try {
          postsServices
            .addPost("api/v1/dashboard/disbursement-history", payload)
            .then((response) => {
              if (showAllData === false) {
                const limitedRecords = response?.data?.data.slice(0, 5);
                setRows(limitedRecords);
              } else {
                setRows(response?.data?.data);
              }
              setIsLoading(false);
            })
            .catch((error) => {});
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchDataHistoryTable();
  }, [selectedLoanNumber]);
  const allcolumns: Column[] = [
    {
      id: "date",
      label: "Date",
      format: (rowData: RowData) =>
        rowData?.date ? formatDate(String(rowData?.date)) : "-",
    },
    {
      id: "party",
      label: "Beneficiary Party",
      format: (rowData: RowData) =>
        rowData?.party != null
          ? String(rowData?.party) == "CS"
            ? "Customer"
            : String(rowData?.party)
          : "-",
    },
    // {
    //   id: "paymentType",
    //   label: "Payment Type",
    //   format: (rowData: RowData) =>
    //     rowData?.paymentType != null ? String(rowData?.paymentType) : "-",
    // },
    {
      id: "bank",
      label: "Bank",
      format: (rowData: RowData) =>
        rowData?.bank != null ? String(rowData?.bank) : "-",
    },
    {
      id: "name",
      label: "Beneficiary Name",
      format: (rowData: RowData) =>
        rowData?.name != null ? String(rowData?.name) : "-",
    },
    {
      id: "accountNumber",
      label: "Account Number",
      format: (rowData: RowData) =>
        rowData?.accountNumber != null ? String(rowData?.accountNumber) : "-",
    },
    {
      id: "amount",
      label: "Amount",
      format: (rowData: RowData) =>
        typeof rowData.amount === "number"
          ? `₹ ${(rowData.amount / 100).toFixed(2)}`
          : "-",
    },
    {
      id: "utr",
      label: "UTR",
      format: (rowData: RowData) =>
        rowData?.utrNumber != null ? String(rowData?.utrNumber) : "-",
    },
  ];
  const columns =
    showAllData === false
      ? allcolumns.filter((column) => column.id !== "bank")
      : allcolumns;
  const handleChangePage = (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <>
        <Grid
          item
          xs={12}
          style={{ marginBottom: "20px", paddingTop: "10px" }}
          className="tableContainer"
        >
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <StyledTableCellHead key={column.id}>
                      {column.label || "-"}
                    </StyledTableCellHead>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows &&
                  rows.length > 0 &&
                  rows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        {columns.map((column) => (
                          <StyledTableCellBody key={column.id}>
                            {" "}
                            {column.format
                              ? column.format(row)
                              : row[column.id] != null
                              ? String(row[column.id])
                              : "-"}
                          </StyledTableCellBody>
                        ))}
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          {showAllData === true && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Grid>
      </>
    </Box>
  );
};

export default DisbursementHistoryTable;
